import React, { useState } from 'react';
import { Users, Clock, ArrowRightFromLine, UserX, MoreVertical, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';

const HR = () => {
  const [designFilter, setDesignFilter] = useState('design');

  // Top stats
  const topStats = [
    { label: 'TOTAL MEMBERS', value: 30, subLabel: '+2 members', subColor: 'text-green-400', icon: Users },
    { label: 'CLOCK-IN', value: 20, subLabel: '2 members late', subColor: 'text-gray-400', icon: Clock },
    { label: 'CLOCK-OUT', value: 2, subLabel: '', subColor: '', icon: ArrowRightFromLine },
    { label: 'ABSENT', value: 0, subLabel: '', subColor: '', icon: UserX },
  ];

  // Hiring pipeline stages
  const pipelineStages = [
    { id: 'new-applied', label: 'NEW APPLIED', color: 'bg-blue-600' },
    { id: 'screening', label: 'SCREENING', color: 'bg-teal-600' },
    { id: 'interview', label: 'INTERVIEW', color: 'bg-purple-600' },
    { id: 'test', label: 'TEST', color: 'bg-orange-500' },
    { id: 'hired', label: 'HIRED', color: 'bg-green-500' },
  ];

  // Job positions with hiring data
  const jobPositions = [
    {
      id: 1,
      title: 'Head Coach',
      totalApplications: 88,
      pipeline: { newApplied: 18, screening: 8, interview: 3, test: null, hired: null }
    },
    {
      id: 2,
      title: 'Assistant Coach',
      totalApplications: 62,
      pipeline: { newApplied: 22, screening: 12, interview: 5, test: 3, hired: 2 }
    },
    {
      id: 3,
      title: 'Athletic Trainer',
      totalApplications: 54,
      pipeline: { newApplied: 15, screening: 10, interview: 6, test: 2, hired: 1 }
    },
    {
      id: 4,
      title: 'Scout Analyst',
      totalApplications: 45,
      pipeline: { newApplied: 12, screening: 7, interview: 3, test: 1, hired: 1 }
    },
  ];

  // Jobs summary data
  const jobsSummary = {
    total: 130,
    breakdown: [
      { label: 'PUBLISHED', count: 80, color: 'bg-green-500' },
      { label: 'ON HOLD', count: 20, color: 'bg-orange-500' },
      { label: 'INTERNAL', count: 15, color: 'bg-pink-500' },
      { label: 'CLOSED', count: 15, color: 'bg-purple-500' },
    ]
  };

  return (
    <div className="min-h-screen bg-black p-4 md:p-6">
      <div className="max-w-[1800px] mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-white">HR</h1>
          <p className="text-gray-400 mt-1">Human Resources management and employee tracking</p>
        </div>

        {/* Top Stats Cards */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          {topStats.map((stat, idx) => (
            <div key={idx} className="bg-black border border-gray-400 rounded-xl hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300 p-5">
              <div className="flex items-center justify-between mb-3">
                <span className="text-gray-400 text-sm">{stat.label}</span>
                <stat.icon size={20} className="text-gray-500" />
              </div>
              <p className="text-5xl font-bold text-white">{stat.value}</p>
              {stat.subLabel && (
                <p className={`text-sm mt-2 ${stat.subColor}`}>{stat.subLabel}</p>
              )}
            </div>
          ))}
        </div>

        <div className="flex gap-6">
          {/* Main Content - Hiring */}
          <div className="flex-1">
            <div className="bg-black border border-gray-400 rounded-xl hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300 p-6">
              {/* Hiring Header */}
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-white font-semibold text-lg">Hiring</h2>
                <div className="flex items-center gap-3">
                  <button className="flex items-center gap-2 px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors">
                    Design <ChevronDown size={16} />
                  </button>
                  <button className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors">
                    VIEW ALL &gt;
                  </button>
                </div>
              </div>

              {/* Job Positions */}
              <div className="space-y-6">
                {jobPositions.map(job => (
                  <div key={job.id} className="border border-gray-800 rounded-xl p-5">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h3 className="text-white font-semibold">{job.title}</h3>
                        <p className="text-gray-500 text-sm">Total Application: {job.totalApplications}</p>
                      </div>
                      <button className="text-gray-400 hover:text-white">
                        <MoreVertical size={18} />
                      </button>
                    </div>

                    {/* Pipeline Stages */}
                    <div className="grid grid-cols-5 gap-3">
                      {pipelineStages.map((stage, stageIdx) => {
                        const pipelineKey = ['newApplied', 'screening', 'interview', 'test', 'hired'][stageIdx];
                        const count = job.pipeline[pipelineKey];
                        const hasValue = count !== null && count !== undefined;
                        
                        return (
                          <div 
                            key={stage.id}
                            className={`${hasValue ? stage.color : 'bg-gray-700'} rounded-lg p-4 text-center`}
                          >
                            <p className="text-white text-xs font-medium mb-1">{stage.label}</p>
                            <p className="text-white text-2xl font-bold">{hasValue ? count : '-'}</p>
                            <p className="text-white/70 text-xs">Candidates</p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right Sidebar - Jobs Summary */}
          <div className="w-80 flex-shrink-0">
            <div className="bg-black border border-gray-400 rounded-xl hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300 p-5">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-white font-semibold text-lg">Jobs Summary</h2>
                <button className="text-gray-400 hover:text-white">
                  <MoreVertical size={18} />
                </button>
              </div>

              {/* Donut Chart */}
              <div className="relative flex justify-center mb-6">
                <svg viewBox="0 0 100 100" className="w-48 h-48">
                  {/* Background circle */}
                  <circle cx="50" cy="50" r="40" fill="none" stroke="#1f2937" strokeWidth="12" />
                  
                  {/* Published (green) - 80/130 = ~62% */}
                  <circle 
                    cx="50" cy="50" r="40" 
                    fill="none" 
                    stroke="#22c55e" 
                    strokeWidth="12"
                    strokeDasharray="155 251"
                    strokeDashoffset="0"
                    transform="rotate(-90 50 50)"
                  />
                  
                  {/* On Hold (orange) - 20/130 = ~15% */}
                  <circle 
                    cx="50" cy="50" r="40" 
                    fill="none" 
                    stroke="#f97316" 
                    strokeWidth="12"
                    strokeDasharray="39 251"
                    strokeDashoffset="-155"
                    transform="rotate(-90 50 50)"
                  />
                  
                  {/* Internal (pink) - 15/130 = ~12% */}
                  <circle 
                    cx="50" cy="50" r="40" 
                    fill="none" 
                    stroke="#ec4899" 
                    strokeWidth="12"
                    strokeDasharray="29 251"
                    strokeDashoffset="-194"
                    transform="rotate(-90 50 50)"
                  />
                  
                  {/* Closed (purple) - 15/130 = ~12% */}
                  <circle 
                    cx="50" cy="50" r="40" 
                    fill="none" 
                    stroke="#a855f7" 
                    strokeWidth="12"
                    strokeDasharray="29 251"
                    strokeDashoffset="-223"
                    transform="rotate(-90 50 50)"
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-4xl font-bold text-white">{jobsSummary.total}</span>
                  <span className="text-gray-400 text-sm">TOTAL JOBS</span>
                </div>
              </div>

              {/* Legend */}
              <div className="space-y-3">
                {jobsSummary.breakdown.map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className={`w-3 h-3 rounded-full ${item.color}`}></span>
                      <span className="text-gray-400">{item.label}</span>
                    </div>
                    <span className="text-white font-medium">{item.count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HR;
